import { Briefcase, FileText, Palette, Building2, Plug, FolderPlus } from 'lucide-react';
import type { NavigationItem } from '../types/SideMenu.types';

export type StartupNavSection =
  | 'home'
  | 'work-scopes'
  | 'artifacts'
  | 'files'
  | 'design-system'
  | 'startup-profile'
  | 'integrations';

/**
 * Build the startup-level sidebar nav items from a single `activeSection` and
 * per-section navigation callbacks. Each callback is optional; sections without a
 * callback render disabled.
 */
export const buildStartupNavItems = (opts: {
  activeSection: StartupNavSection;
  onNavigateToHome?: () => void;
  onNavigateToWorkScopes?: () => void;
  onNavigateToArtifacts?: () => void;
  onNavigateToFiles?: () => void;
  onNavigateToDesignSystem?: () => void;
  onNavigateToStartupProfile?: () => void;
  onNavigateToIntegrations?: () => void;
  /** Optional translator (i18n). Falls back to English labels when omitted. */
  t?: (key: string) => string;
}): NavigationItem[] => {
  const tr = (key: string, fallback: string) => (opts.t ? opts.t(key) : fallback);
  return [
  // Home is hidden for now — users land directly on Work Scopes. Re-add this
  // item (and the `Home` icon import) to bring the Home tab back.
  {
    id: 'work-scopes',
    label: tr('nav.workScopes', 'Work Scopes'),
    icon: Briefcase,
    isActive: opts.activeSection === 'work-scopes',
    disabled: !opts.onNavigateToWorkScopes,
    type: 'navigation',
    testId: 'sidemenu-work-scopes',
    onClick: opts.onNavigateToWorkScopes,
  },
  {
    id: 'artifacts',
    label: tr('nav.artifacts', 'Artifacts'),
    icon: FileText,
    isActive: opts.activeSection === 'artifacts',
    disabled: !opts.onNavigateToArtifacts,
    type: 'navigation',
    testId: 'sidemenu-artifacts',
    onClick: opts.onNavigateToArtifacts,
  },
  {
    id: 'files',
    label: tr('nav.knowledgeHub', 'Knowledge Hub'),
    icon: FolderPlus,
    isActive: opts.activeSection === 'files',
    disabled: !opts.onNavigateToFiles,
    type: 'navigation',
    testId: 'sidemenu-files',
    onClick: opts.onNavigateToFiles,
  },
  {
    id: 'design-system',
    label: tr('nav.designSystem', 'Design system'),
    icon: Palette,
    isActive: opts.activeSection === 'design-system',
    disabled: !opts.onNavigateToDesignSystem,
    type: 'navigation',
    testId: 'sidemenu-design-system',
    onClick: opts.onNavigateToDesignSystem,
  },
  {
    id: 'startup-profile',
    label: tr('nav.startupProfile', 'Startup Profile'),
    icon: Building2,
    isActive: opts.activeSection === 'startup-profile',
    disabled: !opts.onNavigateToStartupProfile,
    type: 'navigation',
    testId: 'sidemenu-startup-profile',
    onClick: opts.onNavigateToStartupProfile,
  },
  {
    id: 'integrations',
    label: tr('nav.integrations', 'Integrations'),
    icon: Plug,
    isActive: opts.activeSection === 'integrations',
    disabled: !opts.onNavigateToIntegrations,
    type: 'navigation',
    testId: 'sidemenu-integrations',
    onClick: opts.onNavigateToIntegrations,
  },
  ];
};
